import { Dispatch } from 'redux';
import { createAction } from '@reduxjs/toolkit';
import { omit } from 'lodash';
import {
  IPS,
  updateIPS,
  createIPS,
  fetchRTQListWithQIDs,
  RawRTQ,
  RTQ,
  saveRTQ,
  fetchRTQWithContactQID
} from '@qwealth/qdata';
import { errorHandler } from 'services/axiosService';
import { AppThunkDispatch, RootState } from '../store';
import { changeRtqLevelApi, initRtqApi, portfolioManagerApprovalApi } from '../data-layer/rtq';
import { fetchProposalPdf, fetchProposalMandatesAndModels, fetchRtqNoteFeedback } from 'data/data-layer/rtq';
import { Proposal, ProposalResponse } from 'data/models/RtqProposal';
import { NoteAssessment } from 'data/models/RtqFormModel';
import { ChangeRtqLevelPayload } from '../models/ChangeRtqLevel';

const ADD_RTQ = 'ADD_RTQ';
const LOAD_RTQ = 'LOAD_RTQ';
const ADD_IPS_TO_RTQ = 'ADD_IPS_TO_RTQ';
const UPDATE_KYC_RTQ = 'UPDATE_KYC_RTQ';

export const addRtq = createAction<RTQ>(ADD_RTQ);
export const loadHouseholdRtqs = createAction<Record<string, RTQ[]>>(LOAD_RTQ);
export const addIpsToRtq = createAction<IPS>(ADD_IPS_TO_RTQ);
export const updateKycRtq = createAction<RTQ>(UPDATE_KYC_RTQ);

let loading = false;
/**
 * @deprecated legacy pattern that's very hairy for household vs individual RTQs
 */
export const loadHouseholdRTQ = (householdId: string, memberIds: Array<string>, reload?: boolean) => async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
  const existingQIDs = Object.keys(getState().RTQ);
  if ((loading || [householdId, ...memberIds].every(qid => existingQIDs.includes(qid))) && !reload) {
    return Promise.resolve();
  }
  const fallbackState = {
    [householdId]: [],
    ...memberIds.reduce((map, qid) => {
      if (qid) {
        map[qid] = [];
      }
      return map
    }, {})
  };
  try {
    loading = true;
    const rtqMap = await fetchRTQListWithQIDs(householdId, memberIds);
    dispatch(loadHouseholdRtqs({ ...fallbackState, ...rtqMap }));
  } catch (e) {
    dispatch(loadHouseholdRtqs(fallbackState));
    await errorHandler(dispatch, 'RTQ load failed')(e);
  } finally {
    loading = false;
  }
};

export const loadLegalEntityRTQs = (qid: string, reload?: boolean) => async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
  const existingQIDs = Object.keys(getState().RTQ);
  if (loading || (existingQIDs.includes(qid) && !reload)) {
    return Promise.resolve();
  }
  return fetchRTQWithContactQID(qid)
    .then((rtqs) => {
      let rtqArray: RTQ[] = [];
      if (rtqs) {
        if (Array.isArray(rtqs)) {
          rtqArray = rtqs;
        } else {
          rtqArray = [rtqs];
        }
      }
      dispatch(loadHouseholdRtqs({ [qid]: rtqArray }));
    })
    .finally(() => {
      loading = false;
    });
}

export const initRtq = (qid: string, householdQid: string) => async (dispatch: AppThunkDispatch): Promise<void> =>
  await initRtqApi(qid, householdQid).catch(errorHandler(dispatch, 'Failed to initialize RTQ'));

export const updateRTQ = (rtq: Partial<RTQ>, key?: string) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    try {
      // removes them from the save, but will be added back below
      const fieldsToOmit = ['ips', 'isExistingAccount', 'primaryAddressIsNotCanada', 'rtqResponses'];
      const rtqOmit = {
        ...omit(rtq, fieldsToOmit),
        accounts: rtq.accounts?.join(),
      } as RawRTQ;

      // clean up nulls
      for (const prop in rtqOmit) {
        if (rtqOmit[prop] == null) {
          delete rtqOmit[prop];
        }
      }

      const updatedRtq = await saveRTQ(rtqOmit);
      fieldsToOmit.forEach(field => {
        const val = rtq[field];
        if (val) {
          updatedRtq[field] = val;
        }
      });
      if (key) {
        dispatch(loadHouseholdRtqs({ [key]: [updatedRtq] }));
      } else {
        dispatch(addRtq(updatedRtq));
      }
      dispatch(updateKycRtq(updatedRtq));
    } catch (e) {
      await errorHandler(dispatch)(e);
    }
  };

export const changeRtqLevel = (rtqId: number, payload: ChangeRtqLevelPayload) => async (dispatch: AppThunkDispatch): Promise<boolean> => {
  return await changeRtqLevelApi(rtqId, payload)
    .then(() => true)
    .catch(err => {
      errorHandler(dispatch, 'Failed to change RTQ level.')(err);
      return false;
    });
}

export const saveIPS = (ips: IPS) => async (dispatch: AppThunkDispatch): Promise<IPS | undefined> => {
  try {
    const savedIPS = (ips.id ? await updateIPS(ips) : await createIPS(ips)) as IPS;
    dispatch(addIpsToRtq(savedIPS));

    return savedIPS;
  } catch (e) {
    await errorHandler(dispatch, 'Saving IPS failed')(e);
  }
};

export const portfolioManagerApproval = (rtqId: number, approvalFlag: boolean) => async (dispatch: AppThunkDispatch): Promise<void> => {
  return await portfolioManagerApprovalApi(rtqId, approvalFlag)
    .catch(errorHandler(dispatch, 'Failed to update portfolio manager approval'));
}

export const getProposalMandatesAndModels =
  () =>
  async (dispatch: Dispatch): Promise<ProposalResponse | null> => {
    try {
      return await fetchProposalMandatesAndModels();
    } catch (error) {
      errorHandler(dispatch, 'Failed fetching mandates and models. Please contact technology@qwealth.com.')(error);
      return null;
    }
  };

export const generateProposalPdf =
  (proposal: Proposal) =>
  async (dispatch: Dispatch): Promise<Blob | null> => {
    try {
      return await fetchProposalPdf(proposal);
    } catch (error) {
      const msg = 'Failed generating KYC Proposal. Please contact technology@qwealth.com and provide form info.'
      errorHandler(dispatch, msg)(error);
      return null;
    }
  };

  export const sendRtqNoteAssessment = async (rtqNoteUuid: string, dispatch: Dispatch): Promise<NoteAssessment | undefined> => {
    try {
      return await fetchRtqNoteFeedback(rtqNoteUuid);
    } catch (error) {
      const msg = `Failed getting the assessment. Please contact technology@qwealth.com, and provide the following information: Note UUID - ${rtqNoteUuid}`;
      errorHandler(dispatch, msg)(error);
      return undefined;
    }
  };
